import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 90, right: 30, bottom: 60, left: 120 };
const CELL_SIZE = 72;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  chartContainer: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '16px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

const FIELD_LABELS = {
  pest365: 'pest365',
  pest365Plus: 'pest365+',
  oneTime: 'oneTime',
  initialServiceVisit: 'ISV',
  mosquitoTickControl: 'MTC',
};

function getFieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

function getCellColor(pValue, significanceLevel) {
  if (pValue == null) return '#f3f4f6';
  if (pValue < significanceLevel) {
    // Significant: green intensity based on how small the p-value is
    // Smaller p-value = more intense green
    const t = Math.max(0, Math.min(1, pValue / significanceLevel));
    const r = Math.round(220 * t + 22 * (1 - t));
    const g = Math.round(240 * t + 163 * (1 - t));
    const b = Math.round(220 * t + 74 * (1 - t));
    return `rgb(${r}, ${g}, ${b})`;
  }
  // Not significant: white to light gray based on how far from threshold
  const t = Math.min(1, (pValue - significanceLevel) / (1 - significanceLevel));
  const v = Math.round(255 - t * 10);
  return `rgb(${v}, ${v}, ${v})`;
}

function getTextColor(pValue, significanceLevel) {
  if (pValue == null) return '#9ca3af';
  if (pValue < significanceLevel * 0.2) return '#ffffff';
  if (pValue < significanceLevel) return '#064e3b';
  return '#6b7280';
}

export default function GrangerCausalityMatrix({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.fields || !data.matrix || data.fields.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { fields, matrix, fStatistics, significanceLevel = 0.05, lags } = data;
    const n = fields.length;

    const width = MARGIN.left + n * CELL_SIZE + MARGIN.right;
    const height = MARGIN.top + n * CELL_SIZE + MARGIN.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Hatching pattern for diagonal cells
    const defs = svg.append('defs');
    const pattern = defs.append('pattern')
      .attr('id', 'granger-diag-hatch')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 6)
      .attr('height', 6)
      .attr('patternTransform', 'rotate(45)');
    pattern.append('rect')
      .attr('width', 6)
      .attr('height', 6)
      .attr('fill', '#f3f4f6');
    pattern.append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1.5);

    // Column headers (effect fields) — rotated labels at top
    fields.forEach((field, ci) => {
      g.append('text')
        .attr('x', ci * CELL_SIZE + CELL_SIZE / 2)
        .attr('y', -12)
        .attr('text-anchor', 'start')
        .attr('transform', `rotate(-45, ${ci * CELL_SIZE + CELL_SIZE / 2}, ${-12})`)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text(getFieldLabel(field));
    });

    // Column axis label
    g.append('text')
      .attr('x', (n * CELL_SIZE) / 2)
      .attr('y', -60)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .attr('letter-spacing', '0.05em')
      .text('EFFECT (predicted)');

    // Row axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -(n * CELL_SIZE) / 2)
      .attr('y', -80)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .attr('letter-spacing', '0.05em')
      .text('CAUSE (predictor)');

    // Row labels (cause fields) and cells
    fields.forEach((rowField, ri) => {
      // Row label
      g.append('text')
        .attr('x', -12)
        .attr('y', ri * CELL_SIZE + CELL_SIZE / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text(getFieldLabel(rowField));

      fields.forEach((colField, ci) => {
        const isDiagonal = ri === ci;
        const pValue = isDiagonal ? null : (matrix[ri] && matrix[ri][ci]);
        const fStat = (!isDiagonal && fStatistics) ? (fStatistics[ri] && fStatistics[ri][ci]) : null;
        const isSignificant = pValue != null && pValue < significanceLevel;

        const cellX = ci * CELL_SIZE;
        const cellY = ri * CELL_SIZE;

        // Cell background
        if (isDiagonal) {
          g.append('rect')
            .attr('x', cellX + 1)
            .attr('y', cellY + 1)
            .attr('width', CELL_SIZE - 2)
            .attr('height', CELL_SIZE - 2)
            .attr('rx', 4)
            .attr('ry', 4)
            .attr('fill', 'url(#granger-diag-hatch)');
        } else {
          // Cell rect
          g.append('rect')
            .attr('x', cellX + 1)
            .attr('y', cellY + 1)
            .attr('width', CELL_SIZE - 2)
            .attr('height', CELL_SIZE - 2)
            .attr('rx', 4)
            .attr('ry', 4)
            .attr('fill', getCellColor(pValue, significanceLevel))
            .attr('stroke', isSignificant ? '#15803d' : '#e5e7eb')
            .attr('stroke-width', isSignificant ? 2 : 1);

          // P-value text
          g.append('text')
            .attr('x', cellX + CELL_SIZE / 2)
            .attr('y', cellY + CELL_SIZE / 2 - (isSignificant ? 4 : 0))
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '12px')
            .attr('font-weight', isSignificant ? 700 : 500)
            .attr('fill', getTextColor(pValue, significanceLevel))
            .text(pValue != null ? pValue.toFixed(3) : '--');

          // Star marker for significant cells
          if (isSignificant) {
            g.append('text')
              .attr('x', cellX + CELL_SIZE / 2)
              .attr('y', cellY + CELL_SIZE / 2 + 13)
              .attr('text-anchor', 'middle')
              .attr('font-size', '10px')
              .attr('font-weight', 700)
              .attr('fill', getTextColor(pValue, significanceLevel))
              .text(pValue < 0.01 ? '***' : pValue < 0.05 ? '**' : '*');
          }

          // Direction arrow for significant pairs
          if (isSignificant) {
            const arrowX = cellX + CELL_SIZE - 10;
            const arrowY = cellY + 10;
            g.append('text')
              .attr('x', arrowX)
              .attr('y', arrowY)
              .attr('text-anchor', 'middle')
              .attr('font-size', '10px')
              .attr('fill', getTextColor(pValue, significanceLevel))
              .attr('opacity', 0.7)
              .text('\u2192'); // right arrow →
          }
        }

        // Diagonal label
        if (isDiagonal) {
          g.append('text')
            .attr('x', cellX + CELL_SIZE / 2)
            .attr('y', cellY + CELL_SIZE / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '10px')
            .attr('fill', '#9ca3af')
            .attr('font-style', 'italic')
            .text('self');
        }

        // Invisible hover rect for tooltip
        g.append('rect')
          .attr('x', cellX)
          .attr('y', cellY)
          .attr('width', CELL_SIZE)
          .attr('height', CELL_SIZE)
          .attr('fill', 'transparent')
          .style('cursor', isDiagonal ? 'default' : 'pointer')
          .on('mouseenter', (event) => {
            if (isDiagonal) {
              setTooltip({
                x: event.clientX + 12,
                y: event.clientY - 10,
                isDiagonal: true,
                field: getFieldLabel(rowField),
              });
              return;
            }
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              isDiagonal: false,
              cause: getFieldLabel(rowField),
              effect: getFieldLabel(colField),
              pValue,
              fStat,
              isSignificant,
              significanceLevel,
              lags,
            });
          })
          .on('mousemove', (event) => {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      });
    });

    // Summary: significant causality arrows below the matrix
    const arrowGroup = g.append('g')
      .attr('transform', `translate(0, ${n * CELL_SIZE + 20})`);

    const significantPairs = [];
    fields.forEach((rowField, ri) => {
      fields.forEach((colField, ci) => {
        if (ri !== ci && matrix[ri] && matrix[ri][ci] != null && matrix[ri][ci] < significanceLevel) {
          significantPairs.push({
            cause: getFieldLabel(rowField),
            effect: getFieldLabel(colField),
            pValue: matrix[ri][ci],
          });
        }
      });
    });

    if (significantPairs.length > 0) {
      arrowGroup.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text('Significant causal links:');

      significantPairs.forEach((pair, i) => {
        arrowGroup.append('text')
          .attr('x', 0)
          .attr('y', 16 + i * 14)
          .attr('font-size', '10px')
          .attr('fill', '#15803d')
          .attr('font-weight', 500)
          .text(`${pair.cause} \u2192 ${pair.effect} (p=${pair.pValue.toFixed(3)})`);
      });
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.fields || !data.matrix || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Granger Causality Matrix</div>
        <div style={styles.subtitle}>Which price fields predict changes in other fields?</div>
        <div style={styles.emptyState}>No causality data available</div>
      </div>
    );
  }

  const { significanceLevel = 0.05, lags } = data;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Granger Causality Matrix</div>
      <div style={styles.subtitle}>
        Which price fields predict changes in other fields?
        {lags != null && (
          <span style={{ marginLeft: '8px', color: '#6b7280' }}>
            (Lags: {lags})
          </span>
        )}
      </div>

      <div style={styles.chartContainer}>
        <svg
          ref={svgRef}
          style={{ width: '100%', height: 'auto' }}
          preserveAspectRatio="xMidYMid meet"
        />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#16a34a' }} />
          p &lt; 0.01 (strong)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#4ade80' }} />
          p &lt; {significanceLevel} (significant)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#f5f5f5', border: '1px solid #e5e7eb' }} />
          p &ge; {significanceLevel} (not significant)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: 'repeating-linear-gradient(45deg, #f3f4f6, #f3f4f6 2px, #d1d5db 2px, #d1d5db 3px)' }} />
          Diagonal (self)
        </span>
        <span style={{ marginLeft: '8px' }}>
          ** = p &lt; {significanceLevel} &nbsp; *** = p &lt; 0.01
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.isDiagonal && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.field} (self)
          </div>
          <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>
            Diagonal cell — a field cannot Granger-cause itself
          </div>
        </div>
      )}
      {tooltip && !tooltip.isDiagonal && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
            {tooltip.cause} &rarr; {tooltip.effect}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>p-value</span>
            <span style={{ fontWeight: 600, color: tooltip.isSignificant ? '#15803d' : '#374151' }}>
              {tooltip.pValue != null ? tooltip.pValue.toFixed(4) : '--'}
            </span>
          </div>
          {tooltip.fStat != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>F-statistic</span>
              <span style={{ fontWeight: 600 }}>{tooltip.fStat.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Significant</span>
            <span style={{ fontWeight: 600, color: tooltip.isSignificant ? '#15803d' : '#dc2626' }}>
              {tooltip.isSignificant ? 'Yes' : 'No'}
            </span>
          </div>
          {tooltip.lags != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Lags</span>
              <span style={{ fontWeight: 600 }}>{tooltip.lags}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '6px', paddingTop: '6px', fontSize: '11px', color: '#6b7280' }}>
            {tooltip.isSignificant
              ? `Changes in ${tooltip.cause} significantly predict future changes in ${tooltip.effect}`
              : `No significant predictive relationship detected`}
          </div>
        </div>
      )}
    </div>
  );
}
